import React, { cloneElement, forwardRef, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import DataDonut from './DataDonut';
import { usePage } from '../../contexts/pageContext';
import { AnimatePresence, motion } from 'framer-motion';
import { responsive } from '../../resources/constantes.json';
import DataTable from './DataTable';
import {
  useIsDesktopBig,
  useIsDesktopReal,
  useIsMobileReal,
  useIsTabletReal,
} from '../../hooks/useMediaQuery';
import useWindowWidth from '../../hooks/useWindowWidth';
import OpenIcon from '../svg/OpenIcon';
import AnimatedNumber from '../AnimatedNumber';
import useLoseFocusOnHover from '../../hooks/useLoseFocusOnHover';

const getCols = ({ windowWidth, open }) => {
  switch (true) {
    // tablet & small laptop
    case windowWidth > responsive.mobile && windowWidth < responsive.desktop: {
      if (windowWidth < 950) return `1fr 1fr 2px ${open ? '3' : '0'}fr`;
      return `repeat(2, minmax(${open ? `25%, 200px` : `50%, 300px`})) 2px ${
        open ? '1' : '0'
      }fr`;
    }
    // mobile
    case windowWidth < responsive.mobile: {
      return '1fr';
    }
    // desktop
    default: {
      return `1fr 1fr 2px ${open ? '3' : '0'}fr`;
    }
  }
};

const ResultsGeneralData = ({ children }) => {
  const pageState = usePage();
  const { data } = pageState;
  const info = data.totales.act;
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobileReal();
  const isDesktop = useIsDesktopReal();
  const isTablet = useIsTabletReal();
  const isDesktopBig = useIsDesktopBig();
  const { width: windowWidth, height: windowHeight } = useWindowWidth();
  const buttonRef = useLoseFocusOnHover();
  const mobileCloseButtonRef = useRef(null);

  return (
    <Container
      ref={buttonRef}
      button={isDesktop}
      onClick={isDesktop ? () => setOpen(!open) : undefined}
    >
      <DataContainer
        animate={{
          height: !open ? '10em' : '14em',
          display: 'grid',
          gridTemplateColumns: getCols({ windowWidth, open }),
        }}
        transition={{ duration: 0.2 }}
        initial={{ height: '10em' }}
      >
        <PlusIcon>
          <OpenIcon open={open} variant="collapse" />
        </PlusIcon>
        <div>
          <DataDonut
            title="MESAS INFORMADAS"
            porc={info.pmesesc}
            parcial={info.mesesc}
            total={info.metota}
            collapseFirstRow={open || isTablet}
            hideDonut={!isDesktopBig && open}
          />
        </div>
        <div>
          <DataDonut
            title="VOTANTES"
            porc={info.pvotant}
            parcial={info.votant}
            total={info.centota}
            collapseFirstRow={open || isTablet}
            hideDonut={!isDesktopBig && open}
          />
        </div>
        <Separator>
          <motion.div
            animate={{ height: open ? '100%' : '0%' }}
            transition={{ duration: 0.2 }}
          />
        </Separator>
        <StatsContainerFather>
          <StatsContainer>
            <DataTable data={data.camaras[0]} open={open} />
          </StatsContainer>
        </StatsContainerFather>
      </DataContainer>
      <DataContainerMobile
        initial={false}
        animate={{
          width: open ? '100%' : '60%',
          display: 'none',
        }}
        transition={{ duration: 0.2 }}
      >
        <PlusIcon
          button={isMobile}
          ref={mobileCloseButtonRef}
          tabIndex={!open ? '-1' : undefined}
          onClick={() => setOpen(!open)}
        >
          <OpenIcon open={open} variant="rotate" />
        </PlusIcon>
        <AnimatePresence>
          {open && (
            <BigContainerMobile
              animate="open"
              initial="closed"
              exit="closed"
              variants={{
                open: {
                  scaleY: 1,
                  translateX: 0,
                },
                closed: {
                  scaleY: 0,
                  translateX: -300,
                },
              }}
              style={
                isMobile && windowHeight < windowWidth
                  ? {
                      overflowY: 'scroll',
                      maxHeight: 'calc(100vh - 0.5em - 94px - 5px)',
                    }
                  : {}
              }
            >
              <div>
                <div style={{ width: '100%' }}>
                  <DataDonut
                    title="MESAS INFORMADAS"
                    porc={info.pmesesc}
                    parcial={info.mesesc}
                    total={info.metota}
                    collapseFirstRow={open}
                  />
                </div>
                <div style={{ width: '100%' }}>
                  <DataDonut
                    title="VOTANTES"
                    porc={info.pvotant}
                    parcial={info.votant}
                    total={info.centota}
                    collapseFirstRow={open}
                  />
                </div>
                <div style={{ width: '100%' }}>
                  <StatsContainer>
                    <DataTable data={data.camaras[0]} open={open} />
                  </StatsContainer>
                </div>
              </div>
            </BigContainerMobile>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {!open && (
            <OpenMobileContainer
              onClick={() => {
                mobileCloseButtonRef.current.focus();
                setOpen(true);
              }}
              button={isMobile}
              animate="show"
              initial="hide"
              exit="hide"
              variants={{
                show: { opacity: 1, translateX: 0 },
                hide: { opacity: 0, translateX: -200 },
              }}
            >
              <p>Mesas informadas</p>
              <p style={{ fontWeight: 700 }}>
                <AnimatedNumber value={info.pmesesc} percentage />
              </p>
            </OpenMobileContainer>
          )}
        </AnimatePresence>
      </DataContainerMobile>
      <ChildrenContainer
        style={{
          right: '-1em',
          overflowX: open ? 'hidden' : 'visible',
        }}
        transition={{ ease: 'linear' }}
        animate={{
          width: open ? '0%' : 'fit-content',
        }}
      >
        {children && cloneElement(children, { hideMenu: open })}
      </ChildrenContainer>
    </Container>
  );
};

const ButtonSwitch = forwardRef(({ button, ...rest }, ref) =>
  button ? <button {...rest} ref={ref} /> : <div {...rest} ref={ref} />
);

const ButtonSwitchMotion = forwardRef(({ button, ...rest }, ref) =>
  button ? (
    <motion.button {...rest} ref={ref} />
  ) : (
    <motion.div {...rest} ref={ref} />
  )
);

const ChildrenContainer = styled(motion.div)`
  position: static;
  // width: fit-content;
  right: -0.5em;
  height: 100%;
  transform: translate(2px, 0px);
  & > div {
    height: 100%;
  }
`;

const Container = styled(ButtonSwitch)`
  position: relative;
  border-radius: 0.8em;
  box-sizing: border-box;
  // max-height: 14em;
  overflow: hidden;
  background-color: #f5f8fd;
  padding: 3em 8em 3em 3em;
  margin: 1em 0;
  width: 100%;
  z-index: 1;
  justify-content: space-between;
  border: 2px solid transparent;
  @media screen and (max-width: ${responsive.maxWidth.desktop}px) {
    padding: 1em;
    padding-right: 6em;
  }
  @media screen and (max-width: ${responsive.maxWidth.tablet}px) {
    overflow: visible;
    height: 100%;
    padding: 0;
    margin: 0.5em 0;
    margin-right: 10px;
    display: flex;
    background-color: transparent;
  }
  :focus:not(:hover) {
    border-color: grey;
  }
  :focus-visible {
    outline: none;
  }
  :hover {
    cursor: pointer;
  }
  & * {
    cursor: pointer !important;
  }
`;

const DataContainer = styled(motion.div)`
  display: grid;
  grid-template-rows: 1fr;
  max-width: ${responsive.desktop}px;
  margin: 0 auto;
  background-color: transparent;
  border-radius: 0.8em;
  & > * {
    height: inherit;
  }
  @media screen and (max-width: ${responsive.maxWidth.tablet}px) {
    display: none !important;
  }
`;

const Separator = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  & > div {
    width: 100%;
    background-color: #164192;
  }
`;

const OpenMobileContainer = styled(ButtonSwitchMotion)`
  width: 80%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 0.5em;
  overflow-x: hidden;
  background-color: transparent;
  & > p {
    color: #164192;
    // font-size: 1.5em;
    line-height: 1em;
  }
`;

const PlusIcon = styled(ButtonSwitch)`
  position: absolute;
  top: calc(50% - 2.5em);
  right: 2.5em;
  border-radius: 100%;
  background-color: #215ccc;
  color: white;
  align-self: center;
  justify-self: center;
  text-align: center;
  height: 5em;
  width: 5em;
  margin-top: auto;
  margin-bottom: auto;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: center;
  align-content: center;
  // font-size: 5em;
  z-index: 20;
  font-weight: 800;
  & svg {
    width: 60%;
    height: 60%;
  }
  & .open-icon {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  @media screen and (max-width: ${responsive.tablet}px) {
    height: 3.5em;
    width: 3.5em;
    top: calc(50% - 1.75em);
    //font-size: 3.5em;
  }
  @media screen and (max-width: ${responsive.mobile}px) {
    // display: none;
    height: 2em;
    width: 2em;
    top: calc(50% - 1em);
    //font-size: 2em;
    margin-top: 0;
  }
`;

const StatsContainerFather = styled.div`
  overflow: hidden;
  display: flex;
  justify-content: flex-start;
  margin-left: 2em;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 1fr;
  color: #164192;
  width: calc(100% - 2em);
  gap: 4em;
  @media screen and (max-width: ${responsive.tablet}px) {
    grid-template-columns: repeat(3, auto);
    gap: 1em;
  }
  @media screen and (max-width: ${responsive.mobile}px) {
    border-left: 0px;
    border-top: 1px solid #164192;
    width: 100%;
    gap: 0;
  }
`;

const DataContainerMobile = styled(motion.div)`
  width: 100%;
  padding-right: 1em;
  padding-left: 2em;
  border-radius: 0 40px 40px 0;
  background-color: #f5f8fd;
  display: none;
  flex-direction: column;
  box-sizing: border-box;
  height: 100%;
  position: relative;
  box-shadow: 0px 2px 4px 0px #00000026;
  :hover {
    cursor: pointer;
  }
  .container {
    width: 100%;
    display: flex !important;
    flex-direction: row;
  }
  @media screen and (max-width: ${responsive.maxWidth.tablet}px) {
    display: flex !important;
    flex-direction: row;
  }
`;

const BigContainerMobile = styled(motion.div)`
  width: calc(100% - 2em);
  left: 1em;
  border-radius: 0.8em;
  background-color: #f5f8fd;
  position: absolute;
  box-sizing: border-box;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 1em;
  z-index: 19;
  box-shadow: 0px 4px 4px 4px rgb(0, 0, 0, 0.26);
  transform-origin: top left !important;
  & > div {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 2em;
  }
`;

export default ResultsGeneralData;
